import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MoleculeDrawingSummary } from '@peruselab/api-client/moleculeDrawings';
import { Icon } from '../Icon';
// #794 -- each card is one drawing, so it shows that drawing (text, shapes, arrows, layout).
import { DrawingThumbnail, drawingThumbnailSvg } from '../DrawingThumbnail';
import { StructureZoomDialog, ZoomablePicture, type ZoomPicture } from '../StructureZoomDialog';
import { FavouriteHeart } from '../FavouriteHeart';
import type { MyDrawingsFeed as Feed } from '../../hooks/useMyDrawingsFeed';
import { useLoadMoreOnApproach } from '../../hooks/useLoadMoreOnApproach';
import { useDragSource } from '../../dnd/DragDrop';
import type { PaperPlacement, StructureDragPayload } from '../../dnd/payloads';
import { usePaperAdditions } from './paperAdditions';
import { AddToPaperButton, useAddToPaper } from './AddToPaperButton';

interface MyDrawingsFeedProps {
  readonly feed: Feed;
  readonly onOpen: (id: string) => void;
  readonly openDisabled?: boolean | undefined;
  /** #837 -- set in the feed's Archived view: each card offers Restore. */
  readonly onRestore?: ((drawing: MoleculeDrawingSummary) => void) | undefined;
}

// #682 -- the user's own drawings as cards in the results mosaic (beside the empty drawing
// paper), the way the legacy app greeted a sign-in. Same card anatomy as a search result
// (SearchResultsList): thumbnail, an overlay line (here "Updated …" instead of a match score) and
// a footer that opens the drawing -- and (#715) adds it to the paper in one undo step, where
// opening replaces the paper with the saved drawing. More pages load as the "Load more" button nears the viewport,
// and the button itself does the same for keyboard / screen-reader users.
export function MyDrawingsFeed({ feed, onOpen, openDisabled, onRestore }: MyDrawingsFeedProps) {
  const { t } = useTranslation();
  const { hasMore, loadMore, status, drawings } = feed;
  const loadMoreRef = useLoadMoreOnApproach<HTMLButtonElement>(hasMore, drawings.length, loadMore);

  return (
    <>
      {drawings.map((drawing) => (
        <MyDrawingCard
          key={drawing.id}
          drawing={drawing}
          onOpen={onOpen}
          openDisabled={openDisabled}
          onRestore={onRestore}
        />
      ))}
      {hasMore ? (
        <button
          ref={loadMoreRef}
          type="button"
          className="search-results-show-more icon-button"
          data-testid="my-drawings-load-more"
          disabled={status.state === 'loading'}
          onClick={loadMore}
          aria-label={t('myDrawings.loadMore')}
          title={t('myDrawings.loadMore')}
        >
          <Icon name="expandMore" />
        </button>
      ) : null}
    </>
  );
}

function MyDrawingCard({
  drawing,
  onOpen,
  openDisabled,
  onRestore
}: {
  readonly drawing: MoleculeDrawingSummary;
  readonly onOpen: (id: string) => void;
  readonly openDisabled?: boolean | undefined;
  readonly onRestore?: ((drawing: MoleculeDrawingSummary) => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const title = drawing.title?.trim() || t('myDrawings.untitled');
  // #700 -- a drawing drags onto the paper (or into a reaction box): its structure is added there.
  // #715 -- the footer's Add to paper does the same (one undo step, so Undo takes it back off).
  const additions = usePaperAdditions();
  const addDrawing = useMemo(
    () =>
      additions
        ? async (_isomerId?: string, placement?: PaperPlacement) => {
            await additions.addDrawing(drawing.id, placement);
            return null;
          }
        : undefined,
    [additions, drawing.id]
  );
  const adding = useAddToPaper(addDrawing);
  const { add } = adding;
  const payload = useMemo<StructureDragPayload | null>(
    () =>
      additions ? { kind: 'structure', label: title, place: (placement) => void add(undefined, placement) } : null,
    [add, additions, title]
  );
  const drag = useDragSource(
    payload,
    drawing.thumbnailMoleculeId ? (
      <DrawingThumbnail drawingId={drawing.id} version={drawing.updatedAt} size={72} ink="paper" />
    ) : (
      <span className="dnd-ghost-chip">
        <Icon name="draw" size="1.5rem" />
      </span>
    )
  );
  // #984 -- click or tap the drawing to see it larger, as on a result card.
  const [enlarged, setEnlarged] = useState(false);
  const picture = useMemo<ZoomPicture>(
    () => ({ kind: 'svg', load: () => drawingThumbnailSvg(drawing.id, drawing.updatedAt, 'paper') }),
    [drawing.id, drawing.updatedAt]
  );
  return (
    <div className="search-result-card my-drawing-card" data-testid="my-drawing-card" {...drag}>
      <div className="search-result-thumb">
        {drawing.thumbnailMoleculeId ? (
          <ZoomablePicture name={title} onOpen={() => setEnlarged(true)}>
            <DrawingThumbnail drawingId={drawing.id} version={drawing.updatedAt} size={72} ink="paper" />
          </ZoomablePicture>
        ) : (
          <span className="my-drawing-placeholder" aria-hidden="true">
            <Icon name="image" size="2.5rem" />
          </span>
        )}
        {/* #837 -- pins the drawing to Templates (the File drawer's Recent drawings list did). */}
        <FavouriteHeart favourite={{ kind: 'drawing', id: drawing.id }} name={title} />
      </div>
      {enlarged ? <StructureZoomDialog picture={picture} name={title} onClose={() => setEnlarged(false)} /> : null}
      <div className="search-result-card-overlay-top my-drawing-overlay-top">
        <span className="search-result-card-label">
          {t('myDrawings.updated', { when: relativeTime(drawing.updatedAt, i18n.language) })}
        </span>
        {/* #886 Q6: how many comments it has (ADR 0016). */}
        {drawing.commentCount ? (
          <span className="search-result-card-label my-drawing-comments" data-testid="my-drawing-comments">
            <Icon name="forum" size="0.9rem" />
            <span>{t('discussions.cardCount', { count: drawing.commentCount })}</span>
          </span>
        ) : null}
      </div>
      <div className="search-result-card-footer my-drawing-footer">
        <button
          type="button"
          className="my-drawing-open"
          aria-label={t('myDrawings.open', { title })}
          title={t('myDrawings.open', { title })}
          onClick={() => onOpen(drawing.id)}
          disabled={openDisabled}
        >
          <span className="search-results-id" data-testid="result-name">
            {title}
          </span>
          <Icon name="openInNew" size="1.1rem" />
        </button>
        {onRestore ? (
          <button
            type="button"
            className="icon-button"
            data-testid="my-drawing-restore"
            aria-label={t('myDrawings.restore', { title })}
            title={t('myDrawings.restore', { title })}
            onClick={() => onRestore(drawing)}
            disabled={openDisabled}
          >
            <Icon name="unarchive" size="1.1rem" />
          </button>
        ) : null}
        {additions ? (
          <AddToPaperButton moleculeId={drawing.thumbnailMoleculeId ?? ''} adding={adding} boxes={additions.boxes} />
        ) : null}
      </div>
    </div>
  );
}

const UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60]
];

/** "2 days ago", "just now" style, in the UI language. */
export function relativeTime(iso: string, language: string, now: number = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const format = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, 'second');
}
